import { IRole, PermissionsConfigResponse as DamPermissionsConfigResponse } from '../types/damTypes'; // Antag att dessa typer finns

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api';

// Funktion för att hämta auth token (exempel)
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken'); // Anpassa efter hur du lagrar token
};

interface ApiResponse<T> {
  data: T;
  error?: string;
  message?: string;
}

// Helper för API-anrop
const fetchApi = async <T>(
  url: string, 
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    (headers as any)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, { ...options, headers });
    const responseData = await response.json();

    if (!response.ok) {
      return { 
        data: null as T, 
        error: responseData.message || 'API request failed', 
        message: responseData.message 
      };
    }
    return { data: responseData, message: responseData.message };
  } catch (error: any) {
    console.error('API call error:', error);
    return { data: null as T, error: error.message || 'Network or parsing error' };
  }
};

export const getRoles = async (): Promise<ApiResponse<IRole[]>> => {
  return fetchApi<IRole[]>(`${API_BASE_URL}/roles`);
};

export const getRoleById = async (id: string): Promise<ApiResponse<IRole>> => {
  return fetchApi<IRole>(`${API_BASE_URL}/roles/${id}`);
};

export const createRole = async (roleData: Partial<IRole>): Promise<ApiResponse<IRole>> => {
  return fetchApi<IRole>(`${API_BASE_URL}/roles`, {
    method: 'POST',
    body: JSON.stringify(roleData),
  });
};

export const updateRole = async (id: string, roleData: Partial<IRole>): Promise<ApiResponse<IRole>> => {
  return fetchApi<IRole>(`${API_BASE_URL}/roles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(roleData),
  });
};

export const deleteRole = async (id: string): Promise<ApiResponse<{ message: string }>> => {
  return fetchApi<{ message: string }>(`${API_BASE_URL}/roles/${id}`, {
    method: 'DELETE',
  });
};

export const getPermissionsConfig = async (): Promise<ApiResponse<DamPermissionsConfigResponse>> => {
  try {
    const response = await fetchApi<any>(`${API_BASE_URL}/roles/config/permissions`);
    
    if (response.error) {
      return response as ApiResponse<DamPermissionsConfigResponse>;
    }
    
    // Transform backend format to expected frontend format
    const allPermissions: Array<{ id: string; description: string }> = [];
    const groupedPermissions: Record<string, any> = {};
    
    // Process allPermissionsList and transform it to expected format
    if (response.data.allPermissionsList && Array.isArray(response.data.allPermissionsList)) {
      response.data.allPermissionsList.forEach((permission: string) => {
        allPermissions.push({
          id: permission,
          description: permission // Using permission as description as a fallback
        });
      });
    }
    
    // Process groupedPermissions and transform it to expected format
    if (response.data.groupedPermissions) {
      Object.entries(response.data.groupedPermissions).forEach(([groupKey, permissionsObj]) => {
        if (typeof permissionsObj === 'string') {
          groupedPermissions[groupKey] = {
            label: groupKey,
            permissions: [{
              id: permissionsObj,
              description: groupKey
            }]
          };
          return;
        }

        if (typeof permissionsObj === 'object' && permissionsObj !== null) {
          const permissionsArray = Object.entries(permissionsObj as Record<string, string>).map(
            ([key, value]) => ({
              id: value,
              description: key
            })
          );
          
          groupedPermissions[groupKey] = {
            label: groupKey,
            permissions: permissionsArray
          };
        }
      });
    }
    
    // Return transformed data in expected format
    return {
      data: {
        allPermissions,
        groupedPermissions
      }
    };
    
  } catch (error: any) {
    console.error('Error getting permissions config:', error);
    return { 
      data: null as any, 
      error: error.message || 'Kunde inte hämta behörighetskonfiguration' 
    };
  }
}; 